import type {
  ArchiveDocumentationRequest,
  EditDocumentationVersionRequest,
  MoveDocumentationRequest,
  RestoreDocumentationRequest,
} from 'shared/domain/messages/documentation/eventMessages';
import { DocumentationEditModel } from 'shared/domain/documentation/documentationModel';
import { documentationContract, type DocumentationSignedUrls } from 'shared/domain/documentation/documentationContract';
import { readDocumentationSignedUrls } from 'shared/domain/documentation/documentationSignedUrls';
import { broadcastHasAllDocumentations } from 'serviceWorker/broadcasts/documentations';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import { ConfigData } from 'serviceWorker/db/config';
import { ensureAuthenticatedConfig } from 'serviceWorker/services/config';
import * as documentations from 'serviceWorker/db/documentations/documentations';
import * as documentationsService from 'serviceWorker/db/documentations/documentationsService';
import { debugLog } from 'shared/logger/debugLog';
import { documentationDeleter } from './deleteDocumentation';
import { documentationEditor } from './editDocumentation';
import {
  fetchDocumentationsPaged,
  fetchUpdatedDocumentations,
} from './fetchDocumentations';
import { documentationMover } from './moveDocumentation';
import { documentationRestorer } from './restoreDocumentation';
import type { GetDocumentationSignedRequestEventDetail } from 'serviceWorker/api/types';
import { logWithRethrow } from 'serviceWorker/db/helpers';
import { LogLevel } from 'shared/types/logger';
import {
  buildSignedRequestUrlForDocumentation,
  pullSignedRequest,
} from '../signedRequest';
import { documentationVersionEditor } from './editDocumentationVersion';
import { createAbortOnLogout } from 'serviceWorker/helpers/abortableServiceHandler';
import { Services } from 'shared/domain/messages/message';
import { createSyncedEntity } from '../factories/createSyncedEntity';

export function handleEditDocumentationVersion(
  detail: EditDocumentationVersionRequest
): void {
  debugLog('onEditDocumentationVersion', detail);
  documentationVersionEditor.execute(detail);
}

const documentationAbortOnLogout = createAbortOnLogout('concurrent');

/** Download and sync of the documentations; the source's `sync` is its entry point. */
export const documentationsSync = createSyncedEntity({
  entityName: Services.DOCUMENTATIONS,
  contract: documentationContract,
  repository: { ...documentations, addBatch: documentations.fastAddBatch },
  service: documentationsService,
  fetchPaged: fetchDocumentationsPaged,
  fetchUpdated: fetchUpdatedDocumentations,
  publishCollection: completeDocumentationsSync,
});

function completeDocumentationsSync(): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  broadcastHasAllDocumentations();
}

export function handleEditDocumentation(detail: DocumentationEditModel): Promise<void> {
  debugLog('editDocumentation', detail);
  return documentationEditor.execute(detail);
}

export function handleMoveDocumentation(
  detail: MoveDocumentationRequest
): void {
  debugLog('moveDocumentation', detail);
  documentationMover.execute(
    detail.parentId,
    detail.documentations,
    detail.directories
  );
}

export function handleDeleteDocumentations(
  detail: ArchiveDocumentationRequest
): void {
  debugLog('deleteDocumentation', detail);
  documentationDeleter.execute(
    detail.documentations,
    detail.directories,
    detail.signature
  );
}

export function handleRestoreDocumentations(
  detail: RestoreDocumentationRequest
): void {
  debugLog('restoreDocumentation', detail);
  documentationRestorer.execute(
    detail.documentations,
    detail.directories ?? [],
    detail.signature
  );
}

/** Signed URLs of a documentation version; rejects when the server does not return them. */
export async function getDocumentationSignedUrls(
  detail: GetDocumentationSignedRequestEventDetail
): Promise<DocumentationSignedUrls> {
  const abortController = documentationAbortOnLogout.createController();
  debugLog('getDocumentationSignedRequestHandler', detail);
  const { documentationId, versionId } = detail;

  try {
    const setup: ConfigData | undefined = await ensureAuthenticatedConfig();
    const fullUrl = buildSignedRequestUrlForDocumentation(setup, documentationId, versionId);
    const response: Response = await pullSignedRequest(setup, abortController, fullUrl);
    const parsedResponse = await response.json().catch((e) =>
      logWithRethrow({
        msg: 'Could not parse documentation response',
        logLevel: LogLevel.ERROR,
        errorObj: e,
        additionalInfo: { response },
      })
    );

    debugLog(parsedResponse);
    const signedUrls = response.status === 200 ? readDocumentationSignedUrls(parsedResponse) : undefined;
    if (!signedUrls) {
      throw new Error(`No signed URL for documentation ${documentationId} (status ${response.status})`);
    }
    return signedUrls;
  } finally {
    documentationAbortOnLogout.releaseController(abortController);
  }
}
